<template>
  <div class="login-coniner">
    <picture class="logo-container">
      <source media="(max-width: 767px)" :srcset="loginSp" />
      <source media="(min-width: 768px)" :srcset="loginPc" class="login-logo" />
      <img :src="loginSp" alt="skyviz" class="login-logo" />
    </picture>
    <div class="login-input">
      <Form @submit="onSubmit" v-slot="{ meta }" autocomplete="off">
        <ul>
          <li>
            <label for="loginName" class="label-l"> ユーザーID </label>
            <InputText name="loginName" rules="required" type="text" v-model="loginId" />
            <p class="asterisk">※半角英数字</p>
            <ErrorMessageBox name="loginName" class="field-error" />
          </li>
          <li class="password">
            <div class="password-container">
              <label for="password" class="label-l"> パスワード </label>
              <InputText
                name="loginPassword"
                id="login_password"
                rules="required"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                :class="{ 'is-error': serverErrorMessage }"
                v-model="password"
              />
              <button
                type="button"
                id="togglePassword"
                class="toggle-password"
                @click="togglePassword"
                rules="required"
                aria-label="パスワード表示切替"
              >
                <span class="visibility">
                  <img
                    :src="showPassword ? pathOpen : pathClosed"
                    :alt="showPassword ? 'パスワードを非表示' : 'パスワードを表示'"
                    id="icon-image"
                  />
                </span>
              </button>
            </div>
            <ErrorMessageBox name="loginPassword" class="field-error" />
          </li>
        </ul>
        <button
          class="button-primary"
          type="submit"
          :class="{ 'is-disabled': !meta.valid || isSubmitting }"
          :disabled="!meta.valid || isSubmitting"
          id="loginButton"
        >
          ログインする
        </button>
      </Form>
      <ErrorMessageBox
        v-if="serverErrorMessage"
        :message="serverErrorMessage"
        class="login-action-error-message"
      />
    </div>
    <div class="login-footer">
      <ul>
        <li class="usage-note" id="open-modal" @click="openNotice" style="cursor: pointer">
          ご利用上の注意
        </li>
        <li class="copyright">Copyright &copy; Japan Weather Association. All Rights Reserved.</li>
      </ul>
    </div>
    <NoticeModal />
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { Form } from 'vee-validate'
import { useLoginLogic } from '@/composables/useLoginLogic'
import InputText from '@/components/form/InputText.vue'
import ErrorMessageBox from '@/components/form/ErrorMessageBox.vue'
import NoticeModal from '@/components/modal/NoticeModal.vue'
import { useFlightAreaStore } from '@/stores/flightArea'

// 画像import
import loginSp from '@/assets/images/logo/Login_smartphone.png'
import loginPc from '@/assets/images/logo/Login_PC.png'
import iconOpen from '@/assets/images/icon/icon_visibility_off.svg'
import iconClosed from '@/assets/images/icon/icon_visibility.svg'

const flightAreaStore = useFlightAreaStore()
const loginId = ref(localStorage.getItem('loginId') || '')
const password = ref('')

const { onSubmit: loginSubmit } = useLoginLogic()
const isSubmitting = ref(false)
const serverErrorMessage = ref<string>('')

const showPassword = ref(false)
const pathOpen = iconOpen
const pathClosed = iconClosed

const togglePassword = () => {
  showPassword.value = !showPassword.value
}

const onSubmit = async (values: Record<string, unknown>) => {
  if (isSubmitting.value) return
  isSubmitting.value = true
  serverErrorMessage.value = ''
  flightAreaStore.clearStore()
  try {
    const result = await loginSubmit({
      loginName: values.loginName as string,
      password: values.loginPassword as string,
    })
    if (result?.serverErrorMessage) {
      serverErrorMessage.value = result.serverErrorMessage
    } else {
      localStorage.setItem('loginId', values.loginName as string)
    }
  } finally {
    isSubmitting.value = false
  }
}

import { useNoticeModalStore } from '@/stores/noticeModal'
const store = useNoticeModalStore()
function openNotice() {
  store.open()
}

// ログイン画面用の body クラス付与
onMounted(() => {
  document.body.classList.add('login')
  password.value = '' // パスワードは毎回未入力で表示
})

onUnmounted(() => {
  document.body.classList.remove('login')
  document.body.classList.remove('no-scroll')
})
// モーダルが開かれているとき、スクロール禁止
watch(
  () => store.visible,
  (open) => {
    if (open) {
      document.body.classList.add('no-scroll')
    } else {
      document.body.classList.remove('no-scroll')
    }
  },
  { immediate: true },
)
</script>
<style scoped>
.login-action-error-message {
  margin-top: 12px;
  clear: both;
  display: flex;
  justify-content: center;
  width: 100%;
}

:deep(.error-message.field-error span) {
  justify-content: flex-start;
}
</style>
